export class Node<T> {
	public next: Node<T> | null = null;
	public previous: Node<T> | null = null;

	constructor(public value: T) {}
}

export class LinkedList<T> {
	public head: Node<T> | null = null;
	public tail: Node<T> | null = null;
	public length = 0;

	public has(value: T): [node: Node<T>, index: number] | null {
		let current = this.head;
		let i = 0;

		while (current !== null) {
			if (current.value === value) {
				return [current, i];
			}

			current = current.next;
			i++;
		}

		return null;
	}

	public getNode(index: number): Node<T> | null {
		if (index < 0 || index >= this.length) {
			throw new Error("Index out of bounds");
		}

		let current = this.head;
		let i = 0;

		while (i < index && current !== null) {
			current = current.next;
			i++;
		}

		return current;
	}

	public insert(value: T): void {
		this.insertAt(value, this.length);
	}

	public insertAt(value: T, index: number): void {
		const node = new Node(value);

		// empty list
		if (this.length === 0) {
			this.head = node;
			this.tail = node;
			this.length++;

			return;
		}

		// insert at the beginning
		if (index === 0) {
			const previousHead = this.head;

			if (previousHead === null) {
				throw new Error("List is empty");
			}

			// Reconnect
			this.head = node;
			this.head.next = previousHead;
			previousHead.previous = this.head;

			this.length++;

			return;
		}

		// insert at the end
		if (index === this.length) {
			const previousTail = this.tail;

			if (previousTail === null) {
				throw new Error("List is empty");
			}

			// Reconnect
			this.tail = node;
			this.tail.previous = previousTail;
			previousTail.next = this.tail;

			this.length++;
			return;
		}

		// insert anywhere else

		let current = this.head;
		let i = 0;

		while (i < index && current !== null) {
			current = current.next;
			i++;
		}

		if (current === null || i < index - 1) {
			throw new Error("Index out of bounds");
		}

		const previous = current.previous;

		if (previous === null) {
			throw new Error("Index out of bounds");
		}

		node.previous = previous;
		node.next = current;

		previous.next = node;
		current.previous = node;

		this.length++;
	}

	public delete(node: Node<T> | null): void {
		if (node === null) {
			return;
		}

		// a --- node --- b
		// a --- b
		const a = node.previous;
		const b = node.next;

		if (a === undefined) {
			return;
		}

		if (b === undefined) {
			return;
		}

		if (a !== null) {
			a.next = b;
		}

		if (b !== null) {
			b.previous = a;
		}

		// x --- b
		if (a === null) {
			this.head = b;
		}

		// a --- x
		if (b === null) {
			this.tail = a;
		}

		this.length--;
	}

	public deleteAt(index: number): void {
		if (index < 0 || index >= this.length) {
			throw new Error("Index out of bounds");
		}

		const node = this.getNode(index);
		this.delete(node);
	}

	*[Symbol.iterator](): Iterator<T> {
		let current = this.head;

		while (current !== null) {
			yield current.value;
			current = current.next;
		}
	}
}
